import React, { useState } from "react";
import { api } from "../../../services/api";
import { useLoadingMessages } from "../useLoadingMessages";

const MIN_CHARS = 40;

type Phase = "description" | "naming";

interface TeamNameStepProps {
  step: number;
  totalSteps: number;
  prompt: string;
  setPrompt: (v: string) => void;
  teamName: string;
  setTeamName: (v: string) => void;
  onStartLogoGeneration: (name: string, description: string) => void;
  onNext: () => void;
  onBack: () => void;
}

const TeamNameStep: React.FC<TeamNameStepProps> = ({
  step, totalSteps,
  prompt, setPrompt,
  teamName, setTeamName,
  onStartLogoGeneration,
  onNext, onBack,
}) => {
  const [phase, setPhase] = useState<Phase>(() => (teamName.trim() ? "naming" : "description"));
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [namesError, setNamesError] = useState("");
  const [descError, setDescError] = useState("");
  const [nameError, setNameError] = useState("");
  const [checkingDesc, setCheckingDesc] = useState(false);
  const [checkingName, setCheckingName] = useState(false);

  const { message: namesMsg, animKey: namesKey } = useLoadingMessages(
    ["Brainstorming names…", "Thinking of something unique…", "Almost there…"],
    loadingNames
  );

  const descReady = prompt.trim().length >= MIN_CHARS;
  const truncatedPrompt = prompt.trim().length > 80
    ? prompt.trim().slice(0, 80).trimEnd() + "…"
    : prompt.trim();

  const handleContinueFromDescription = async () => {
    if (!descReady) return;
    setDescError("");
    setCheckingDesc(true);
    try {
      const moderation = await api.moderateText(prompt.trim());
      if (moderation.flagged) {
        setDescError("Please rephrase your description — it can't contain explicit, hateful, or derogatory content.");
        return;
      }
    } catch (err) {
      console.error("Failed to moderate team description:", err);
    } finally {
      setCheckingDesc(false);
    }

    setNamesError("");
    setLoadingNames(true);
    setPhase("naming");

    api.generateNames(prompt.trim())
      .then(data => setSuggestions(data.team_names || []))
      .catch(() => setNamesError("Couldn't generate names. Please try again."))
      .finally(() => setLoadingNames(false));
  };

  const handleConfirmName = async () => {
    if (!teamName.trim()) return;
    setNameError("");
    setCheckingName(true);
    try {
      const moderation = await api.moderateText(teamName.trim());
      if (moderation.flagged) {
        setNameError("Please choose a different name — it can't contain explicit, hateful, or derogatory content.");
        return;
      }
    } catch (err) {
      console.error("Failed to moderate team name:", err);
    } finally {
      setCheckingName(false);
    }

    onStartLogoGeneration(teamName, prompt);
    onNext();
  };

  return (
    <div>
      <div className="bk-progress">
        <div className="bk-pip-row">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`bk-pip ${i + 1 < step ? "bk-pip--done" : i + 1 === step ? "bk-pip--active" : ""}`} />
          ))}
        </div>
        <span className="bk-progress-label">Step {step} of {totalSteps}</span>
      </div>

      <div className="bk-eyebrow">[ 01 / Define ]</div>
      <h1 className="bk-title">Define your team.</h1>

      {/* ── Phase 1: Description ── */}
      <div className={`bk-define-block${phase !== "description" ? " bk-define-block--collapsed" : ""}`}>
        <span className="bk-generate-section-label">Describe your team</span>
        {phase === "description" && (
          <p className="bk-desc" style={{ marginBottom: "0.5rem", marginTop: "0.25rem", fontSize: "0.85rem" }}>
            Tell us who you are, what you play, and where.
          </p>
        )}

        {phase !== "description" && (
          <div className="bk-define-summary-row" style={{ marginTop: "0.5rem" }}>
            <span className="bk-define-summary-check">✓</span>
            <span className="bk-define-summary-text">{truncatedPrompt}</span>
          </div>
        )}

        <div
          className={`bk-define-collapse${phase !== "description" ? " bk-define-collapse--closed" : ""}`}
          aria-hidden={phase !== "description"}
        >
          <div>
            <textarea
              className="bk-textarea"
              style={{ minHeight: "90px", marginTop: "0.5rem", marginBottom: "0.5rem" }}
              placeholder="e.g. Junior doctors who play 5-a-side football on Wednesdays in Clapham"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              disabled={phase !== "description"}
              rows={3}
            />
            <div className={`bk-char-hint${descReady ? " bk-char-hint--ok" : ""}`}>
              {prompt.trim().length} / {MIN_CHARS} characters minimum
            </div>
            {descError && <p className="bk-error" style={{ marginTop: "0.5rem" }}>{descError}</p>}
            <div className="bk-actions" style={{ marginTop: "1.25rem" }}>
              <button className="bk-btn-text" onClick={onBack}>← Home</button>
              <button className="bk-btn" onClick={handleContinueFromDescription} disabled={!descReady || checkingDesc}>
                {checkingDesc ? "Checking…" : "Continue →"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Phase 2: Team name ── */}
      {phase === "naming" && (
        <div className="bk-define-block bk-define-reveal">
          <span className="bk-generate-section-label">Team name</span>

          {loadingNames ? (
            <p className="bk-loading-status">
              <span key={namesKey} className="bk-loading-msg">{namesMsg}</span>
            </p>
          ) : namesError ? (
            <p className="bk-error" style={{ marginTop: "0.5rem" }}>{namesError}</p>
          ) : suggestions.length > 0 ? (
            <div className="bk-name-pills" style={{ marginTop: "0.75rem" }}>
              {suggestions.map(name => (
                <button
                  key={name}
                  className={`bk-name-pill ${teamName === name ? "bk-name-pill--selected" : ""}`}
                  onClick={() => setTeamName(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          ) : null}

          <input
            type="text"
            className="bk-name-input"
            style={{ marginTop: "1rem" }}
            placeholder="Or type your own…"
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            maxLength={40}
          />
          {nameError && <p className="bk-error" style={{ marginTop: "0.5rem" }}>{nameError}</p>}
          <div className="bk-actions" style={{ marginTop: "1.25rem" }}>
            <button className="bk-btn-text" onClick={onBack}>← Home</button>
            <button
              className="bk-btn"
              onClick={handleConfirmName}
              disabled={!teamName.trim() || loadingNames || checkingName}
            >
              {checkingName ? "Checking…" : "Confirm Name →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamNameStep;
